import { useState } from 'react';
import { Check, FolderPlus, Send } from 'lucide-react';
import { Button } from '../ui/Button';
import type { PropsCaptura } from './registro';

/**
 * Captura de la primitiva `conexion` COMO PRIMITIVA DEL PARTICIPANTE
 * (Oleada 3 · card sorting). Patrón validado por el mercado (Optimal
 * Workshop, NN/g): UNA tarjeta en foco por vez → elegir una categoría propia
 * o crear una nueva. Nunca un tablero de arrastre libre en 380px.
 *
 * El mazo llega en `captura.opciones` (resuelto por el backend desde la
 * actividad fuente, `opciones_desde`). Cada clasificación es UN aporte
 * `{tarjeta, categoria}`: el agregado de coincidencias se arma con todos los
 * aportes de la sala (C7). Las tarjetas que ya clasificaste (misAportes) no
 * se vuelven a ofrecer.
 */
export function CapturaConexion({
  captura,
  deshabilitado,
  enviando,
  onEnviar,
  misAportes,
}: PropsCaptura) {
  const [categoria, setCategoria] = useState('');
  const [nueva, setNueva] = useState('');

  // Tarjetas ya clasificadas por MÍ (una tarjeta = un aporte).
  const clasificadas = new Set(
    (misAportes ?? [])
      .filter((a) => a.tipo === 'conexion' && typeof a.payload.tarjeta === 'string')
      .map((a) => a.payload.tarjeta as string),
  );
  const pendientes = captura.opciones.filter((op) => !clasificadas.has(op.valor));
  const tarjeta = pendientes[0] ?? null;

  // Mis categorías conocidas (de aportes previos), únicas y en orden de uso.
  const conocidas: string[] = [];
  for (const a of misAportes ?? []) {
    const c = a.payload.categoria;
    if (a.tipo === 'conexion' && typeof c === 'string' && c && !conocidas.includes(c)) {
      conocidas.push(c);
    }
  }

  if (tarjeta === null) {
    return (
      <p className="flex items-center gap-2 rounded-md border border-border bg-surface-sunken px-4 py-3 font-sans text-sm text-content">
        <Check className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
        Clasificaste todas las tarjetas del mazo. Gracias: tu ordenamiento ya
        cuenta para el agregado de la sala.
      </p>
    );
  }

  const elegida = categoria || nueva.trim();
  const enviar = () => {
    if (!elegida) return;
    onEnviar({ tarjeta: tarjeta.valor, categoria: elegida });
    setCategoria('');
    setNueva('');
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="font-sans text-sm text-content-muted" aria-live="polite">
        Tarjeta {captura.opciones.length - pendientes.length + 1} de{' '}
        {captura.opciones.length}
      </p>

      <div className="rounded-lg border border-primary bg-surface-raised px-4 py-5 text-center font-display text-lg font-semibold text-content">
        {tarjeta.etiqueta}
      </div>

      <div className="flex flex-col gap-2">
        <span className="font-sans text-sm font-medium text-content">
          ¿En qué grupo la ponés?
        </span>
        {conocidas.length > 0 && (
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Tus grupos">
            {conocidas.map((c) => {
              const activa = categoria === c;
              return (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={activa}
                  disabled={enviando || deshabilitado}
                  onClick={() => {
                    setCategoria(activa ? '' : c);
                    setNueva('');
                  }}
                  className={`min-h-touch rounded-md border px-3 font-sans text-sm font-medium transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50 ${
                    activa
                      ? 'border-primary bg-surface-sunken text-content'
                      : 'border-border-strong bg-surface-raised text-content-muted hover:border-primary'
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex items-center gap-2">
          <FolderPlus className="h-4 w-4 shrink-0 text-content-muted" aria-hidden="true" />
          <input
            type="text"
            value={nueva}
            onChange={(e) => {
              setNueva(e.target.value);
              setCategoria('');
            }}
            placeholder={conocidas.length ? 'O creá un grupo nuevo…' : 'Nombrá tu primer grupo…'}
            aria-label="Grupo nuevo"
            maxLength={60}
            disabled={enviando || deshabilitado}
            className="min-h-touch w-full rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-base text-content placeholder:text-content-muted transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50"
          />
        </div>
      </div>

      <Button
        variant="primary"
        fullWidth
        loading={enviando}
        disabled={!elegida || deshabilitado}
        onClick={enviar}
        leadingIcon={<Send className="h-5 w-5" aria-hidden="true" />}
      >
        Guardar y pasar a la siguiente
      </Button>
      {!elegida && (
        <p className="font-sans text-sm text-content-muted" aria-live="polite">
          Falta: elegir o crear el grupo de esta tarjeta
        </p>
      )}
    </div>
  );
}
